import {FormProvider, useForm} from "react-hook-form";
import {Box} from "@mui/material";
import {FormInputAutocomplete} from "../../components/FormInputAutocomplete";
import {StyledButton} from "../../providers/layout/LayoutProvider";
import {FormInputText2} from "../../components/FormInputText2";
import {useRecoilValue} from "recoil";
import {LocalizationsAtom} from "../../providers/localization/LocalizationProvider";
import {SpejdersportShopDefault, SpejdersportShopPayload, SpejdersportShopType} from "./SpejderSportShops";
import {BoxProps} from "@mui/material/Box/Box";
import {useEffect, useState} from "react";
import {AxiosRequest} from "../../utility/AxiosRequest";

type FormData = {
   shopNr: string
   receiptNr: string
}

type SpejderSportFormProps = BoxProps & {
   onLookup: (shopNr: string, receiptId: string) => Promise<void>
}

export const SpejderSportForm = ({onLookup, ...rest}: SpejderSportFormProps) => {
   const localizations = useRecoilValue(LocalizationsAtom);
   const [shops, setShops] = useState<SpejdersportShopType[]>([])

   const form = useForm<FormData>({
      mode: "onChange",
      defaultValues: {
         shopNr: '',
         receiptNr: ''
      }
   })

   const {handleSubmit} = form

   const onSubmit = async (data: FormData) => {
      await onLookup(data.shopNr, data.receiptNr)
   }

   /**
    * validates receipt number.
    * Returns true if receipt number contains 7 number and returns false otherwise.
    * @param receiptNr - receipt number to validate.
    * */
   const validate = (receiptNr: string): boolean | undefined => {
      return !receiptNr ? undefined : /^\\d{7}$/.test(receiptNr)
   }

   /** Returns SpejdersportShopType[] if SpejdersportShops are fetched from API.
    * Otherwise, SpejdersportShopDefault.stores are used.
    * */
   useEffect(() => {
      (async function () {
         await AxiosRequest<SpejdersportShopPayload>({
            url: 'https://services.create2stay.com/api/spejdersportstores',
         })
         .then((res) => setShops(res.data.stores))
         .catch(() => setShops(SpejdersportShopDefault.stores))
      })()
   }, [])

   return <Box {...rest}>
      <FormProvider {...form}>
         <form onSubmit={handleSubmit(onSubmit)} id={"spejderform_lookup"}>
            <Box sx={{
               textAlign: 'left',
               '@media only screen and (max-width: 1024px)': {
                  textAlign: 'center'
               },
            }}>
               <FormInputAutocomplete
                  containerStyle={{
                     borderRadius: '10px'
                  }}
                  name={"shopNr"} options={shops.map(shop => ({
                  id: shop.externalId,
                  name: shop.name
               }))} label={localizations.publicPageAlternativeField1}/>
               <Box sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '10px',
                  alignItems: 'center',
                  '@media only screen and (max-width: 1224px)': {
                     flexDirection: 'column',
                     gap: '0',
                  },
                  flex: '1 1 auto'
               }}>
                  <Box sx={{flexGrow: '2'}}>
                     <FormInputText2
                        sx={{
                           marginBottom: '8px',
                           borderRadius: '10px',
                           /***** Removes outer & inner spin buttons from TextField *****/
                           /***** Chrome, Safari, Edge, Opera *****/
                           "input::-webkit-outer-spin-button, input::-webkit-inner-spin-button":
                              {
                                 WebkitAppearance: "none",
                                 margin: 0,
                              },
                           /***** Firefox *****/
                           "input[type=number]": {
                              MozAppearance: "textfield",
                           },
                        }}
                        name={"receiptNr"} label={localizations.publicPageAlternativeField2}
                        error={!validate(form.getValues().receiptNr)}
                        errorMessage={'Kvitteringsnummer skal være et tal bestående af 7 cifre'}
                        type="text"
                        inputProps={{
                           maxLength: 7,
                        }}
                        placeholder="Indtast kvitteringsnummer"
                     />
                  </Box>
                  <Box sx={{display: 'flex', justifyContent: 'right', flexGrow: 1}}>
                     <StyledButton sx={{
                        borderRadius: '10px',
                        width: '100%', whiteSpace: 'nowrap'
                     }} type={"submit"} styletype={"front"}>{localizations.publicPageAlternativeField3}</StyledButton>
                  </Box>
               </Box>
            </Box>
         </form>
      </FormProvider>
   </Box>
}
